import { Maybe, maybe } from '@passionware/monads';
import { getBookingRate } from '../../../../../v1/helpers/byModel/BookingHelper';
import { BookingEntity, BookingEntitySpec } from '../types';
import { ResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { useBooking, useResource } from './useBookingEntity';

/**
 * Calculate the total for a rate with optional quantity
 * This replaces the complex calculation logic from v1 helpers
 */
function calculateRateTotal(
  rate: { amount: number; quantity?: number },
  quantity?: number
): number {
  const qty = quantity ?? rate.quantity ?? 1;
  return rate.amount && qty ? rate.amount * qty : 0;
}

/**
 * Calculate fees total (agency + payroll + processing)
 * This replaces the complex fee calculation logic from v1 helpers
 */
function calculateFeesTotal(
  rateTotal: number,
  fees: { agency: number; payroll: number; processing: number }
): number {
  const agencyFeeTotal = fees.agency ? (rateTotal * fees.agency) / 100 : 0;
  const payrollFeeTotal = fees.payroll
    ? ((rateTotal - agencyFeeTotal) * fees.payroll) / 100
    : 0;
  const subtotal = rateTotal + agencyFeeTotal + payrollFeeTotal;
  const processingFeeTotal = fees.processing
    ? (subtotal * fees.processing) / 100
    : 0;

  return agencyFeeTotal + payrollFeeTotal + processingFeeTotal;
}

/**
 * Calculate expenses total from expense array and return individual expense totals
 * This replaces the expense calculation logic from v1 helpers
 */
function calculateExpensesTotal(expenses: any[]): {
  total: number;
  expenseTotals: Array<{ expense: any; total: number; formatted: string }>;
} {
  const expenseTotals = expenses
    .filter(expense => !expense.archived)
    .map(expense => {
      const amount = expense.commit_rate_amount || 0;
      const quantity = expense.commit_rate_quantity || 0;
      const total = amount * quantity;
      const currency = expense.commit_rate_currency || 'USD';

      return {
        expense,
        total,
        formatted: formatCurrency(total, currency)
      };
    });

  const total = expenseTotals.reduce((sum, { total }) => sum + total, 0);

  return { total, expenseTotals };
}

/**
 * Format currency value (simplified version of v1 formatCurrency helper)
 */
function formatCurrency(amountScaled100: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD'
  }).format(amountScaled100 / 100);
}

/**
 * Hook to get effective booking rate that considers booking quantity.
 * Warning: you may see different algos in the app, that combine data with resourcing assignments, but the bottom line is that
 * We during budget calculation we ignore assignment's events (ie 2h/d for 2 days + 3days + one day 10-12). We just read booking dates.
 * @param spec
 */
export function useBookingDependentCalculations(
  spec: BookingEntitySpec,
  resourceService: ResourceService
) {
  const booking = useBooking(spec.bookingId);
  const resource = useResource(resourceService, booking?.contact_id);

  return (source: BookingEntity) => {
    const { estimateValue, actualValue } = source;

    // Calculate estimate totals
    const estimateRateTotal = calculateRateTotal(estimateValue.rate);
    const estimateCurrency = estimateValue.rate.currency;
    const estimateFeesTotal = calculateFeesTotal(
      estimateRateTotal,
      estimateValue.fees
    );
    const estimateExpensesTotal = 0; // No expenses in estimate
    const estimateTotal =
      estimateRateTotal + estimateFeesTotal + estimateExpensesTotal;

    const estimateSummary = {
      total: formatCurrency(estimateTotal, estimateCurrency),
      totalRaw: estimateTotal,
      rates: formatCurrency(estimateRateTotal, estimateCurrency),
      fees: formatCurrency(estimateFeesTotal, estimateCurrency),
      expenses: formatCurrency(estimateExpensesTotal, estimateCurrency)
    };

    // Calculate actual totals
    return {
      estimate: estimateSummary,
      actual: maybe.map(actualValue, actualValue => {
        const bookingRate = getBookingRate(booking, resource) || {
          quantity: 1
        };
        const actualRateTotal = calculateRateTotal(
          actualValue.rate,
          bookingRate.quantity
        );
        const actualCurrency = actualValue.rate.currency;
        const actualFeesTotal = calculateFeesTotal(
          actualRateTotal,
          actualValue.fees
        );
        const { total: actualExpensesTotal, expenseTotals } =
          calculateExpensesTotal(actualValue.expenses);
        const actualTotal =
          actualRateTotal + actualFeesTotal + actualExpensesTotal;

        return {
          bookingRateWithQuantity: {
            ...actualValue.rate,
            quantity: bookingRate.quantity
          },
          total: formatCurrency(actualTotal, actualCurrency),
          totalRaw: actualTotal,
          rates: formatCurrency(actualRateTotal, actualCurrency),
          fees: formatCurrency(actualFeesTotal, actualCurrency),
          expenses: formatCurrency(actualExpensesTotal, actualCurrency),
          expenseTotals // Add per-expense totals
        };
      }),
      currency: estimateCurrency
    };
  };
}
